import { type FC, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import Tooltip from '@components/Tooltip';
import { ArrowLeftIcon } from '@heroicons/react/outline';
import type { EvalConfigDetail } from '@queries/evalConfigs';
import type { EvalWorkflowType, GuidelinePack, PromptPack } from '@root/domain/schemas/evals/eval-config.schema';
import type { UpdateEvalConfigMetaPayload } from '@root/domain/schemas/payload.schema';
import { formatDate } from '@root/helpers';
import Nav from '@root/src/routes';

import { getTabButtonClasses, mutedBadgeClasses, warnBadgeClasses } from '../classes';
import { GuidelinePacksTab } from '../components/GuidelinePacksTab';
import { PromptPacksTab } from '../components/PromptPacksTab';
import { PublishButton } from '../components/PublishButton';
import { SettingsTab } from '../components/SettingsTab';
import { VersionsTab } from '../components/VersionsTab';
import { EVAL_CONFIG_TABS, WORKFLOW_TYPE_LABELS } from '../constants';
import type { EvalConfigTabId } from '../types';

interface EvalConfigProps {
  detail: EvalConfigDetail;
  isSavingMeta: boolean;
  isSavingDraft: boolean;
  isPublishing: boolean;
  onSaveMeta: (payload: Omit<UpdateEvalConfigMetaPayload, 'operation'>) => Promise<void>;
  onSaveDraft: (draft: {
    workflow_type: EvalWorkflowType;
    prompt_packs: PromptPack[];
    guideline_packs: GuidelinePack[];
  }) => Promise<void>;
  onPublish: (changeNote?: string) => Promise<void>;
}

const areSameArrayShallow = <T,>(a: T[], b: T[]) => {
  if (a === b) return true;
  if (a.length !== b.length) return false;
  return a.every((item, i) => item === b[i]);
};

export const EvalConfigView: FC<EvalConfigProps> = ({
  detail,
  isSavingMeta,
  isSavingDraft,
  isPublishing,
  onSaveMeta,
  onSaveDraft,
  onPublish,
}) => {
  const { root, draft, versions } = detail;

  const [activeTab, setActiveTab] = useState<EvalConfigTabId>('settings');

  /**
   * Draft editor state is mirrored locally so users can make multiple edits
   * to prompt/guideline packs before hitting "Save draft".
   */
  const [draftWorkflowType, setDraftWorkflowType] = useState<EvalWorkflowType>(
    draft?.workflow_type ?? root.workflow_type,
  );
  const [promptPacks, setPromptPacks] = useState<PromptPack[]>(draft?.prompt_packs ?? []);
  const [guidelinePacks, setGuidelinePacks] = useState<GuidelinePack[]>(draft?.guideline_packs ?? []);

  useEffect(() => {
    setDraftWorkflowType(draft?.workflow_type ?? root.workflow_type);
    setPromptPacks(draft?.prompt_packs ?? []);
    setGuidelinePacks(draft?.guideline_packs ?? []);
  }, [root.id, draft?.workflow_type, draft?.prompt_packs, draft?.guideline_packs, root.workflow_type]);

  const isPromptDirty = useMemo(
    () =>
      !areSameArrayShallow(promptPacks, draft?.prompt_packs ?? []) ||
      JSON.stringify(promptPacks) !== JSON.stringify(draft?.prompt_packs ?? []) ||
      draftWorkflowType !== (draft?.workflow_type ?? root.workflow_type),
    [promptPacks, draft?.prompt_packs, draft?.workflow_type, root.workflow_type, draftWorkflowType],
  );

  const isGuidelineDirty = useMemo(
    () =>
      !areSameArrayShallow(guidelinePacks, draft?.guideline_packs ?? []) ||
      JSON.stringify(guidelinePacks) !== JSON.stringify(draft?.guideline_packs ?? []),
    [guidelinePacks, draft?.guideline_packs],
  );

  const isDraftDirty = isPromptDirty || isGuidelineDirty;

  const handleSaveDraft = () =>
    onSaveDraft({
      workflow_type: draftWorkflowType,
      prompt_packs: promptPacks,
      guideline_packs: guidelinePacks,
    });

  const updatedByLabel = `Updated ${formatDate(root.updated_at ?? root.created_at, 'DD MMM YYYY')}${
    root.last_updated_by?.name ? ` by ${root.last_updated_by.name}` : ''
  }`;
  const hasSubtext = Boolean(root.description) || Boolean(updatedByLabel);

  return (
    <div className="bg-info-50 flex h-full w-full flex-col">
      <div className="border-info-200 shrink-0 border-b bg-white">
        <div className="mx-auto flex w-full max-w-5xl items-center gap-3 px-6 pt-4 pb-3">
          <Tooltip content="Back to Eval Configs">
            <Link to={Nav.EvalConfigs} className="group" data-testid="link:back-to-eval-configs">
              <div
                className="border-info-300 group-hover:border-info-600 group-hover:bg-info-50 rounded-full border-2 p-1 transition-colors"
                title="Back to Eval Configs"
              >
                <ArrowLeftIcon className="text-info-600 h-4 w-4" strokeWidth="3" />
              </div>
            </Link>
          </Tooltip>
          <div className="flex min-w-0 flex-1 items-center gap-3">
            <h1 className="text-info-900 truncate text-lg font-semibold" title={root.name}>
              {root.name}
            </h1>
            <div className="flex flex-wrap items-center gap-1.5">
              <span className={mutedBadgeClasses} data-testid="badge:workflow-type">
                {WORKFLOW_TYPE_LABELS[root.workflow_type]}
              </span>
              {root.current_version_number !== undefined ? (
                <span className={mutedBadgeClasses} data-testid="badge:current-version">
                  v{root.current_version_number} published
                </span>
              ) : (
                <span className={mutedBadgeClasses} data-testid="badge:no-versions">
                  No versions published
                </span>
              )}
              {root.has_unpublished_draft_changes && (
                <span className={warnBadgeClasses} data-testid="badge:unpublished">
                  Unpublished changes
                </span>
              )}
              {root.archived && (
                <span className={mutedBadgeClasses} data-testid="badge:archived">
                  Archived
                </span>
              )}
            </div>
          </div>
          <div className="shrink-0">
            <PublishButton
              hasUnpublishedChanges={root.has_unpublished_draft_changes}
              isDraftDirty={isDraftDirty}
              isPublishing={isPublishing}
              onPublish={onPublish}
            />
          </div>
        </div>

        {hasSubtext && (
          <div className="mx-auto w-full max-w-5xl px-6 pb-3">
            <p className="text-info-500 text-xs">
              {root.description && <span>{root.description}</span>}
              {root.description && <span className="text-info-300 mx-2">·</span>}
              <span className="text-info-400">{updatedByLabel}</span>
            </p>
          </div>
        )}

        <div className="mx-auto w-full max-w-5xl px-6">
          <div className="flex gap-1" role="tablist" aria-label="Eval config tabs">
            {EVAL_CONFIG_TABS.map((tab) => (
              <button
                key={tab.id}
                type="button"
                role="tab"
                aria-selected={activeTab === tab.id}
                className={getTabButtonClasses(activeTab === tab.id)}
                onClick={() => setActiveTab(tab.id)}
                data-testid={`tab:${tab.id}`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto">
        <div className="mx-auto flex w-full max-w-5xl flex-col gap-6 px-6 py-6">
          {activeTab === 'settings' && <SettingsTab root={root} isSaving={isSavingMeta} onSave={onSaveMeta} />}
          {activeTab === 'prompt_packs' && (
            <PromptPacksTab
              workflowType={draftWorkflowType}
              promptPacks={promptPacks}
              isDirty={isPromptDirty || isGuidelineDirty}
              isSaving={isSavingDraft}
              onChange={setPromptPacks}
              onSave={handleSaveDraft}
            />
          )}
          {activeTab === 'guideline_packs' && (
            <GuidelinePacksTab
              workflowType={draftWorkflowType}
              guidelinePacks={guidelinePacks}
              isDirty={isPromptDirty || isGuidelineDirty}
              isSaving={isSavingDraft}
              onChange={setGuidelinePacks}
              onSave={handleSaveDraft}
            />
          )}
          {activeTab === 'versions' && (
            <VersionsTab
              currentVersionNumber={root.current_version_number}
              hasUnpublishedChanges={root.has_unpublished_draft_changes}
              versions={versions}
            />
          )}
        </div>
      </div>
    </div>
  );
};
